/**
 * Theme Context
 *
 * Provides theme and font switching functionality throughout the application.
 * Persists selections to localStorage.
 */

import { createContext, useContext, useState, useEffect, useMemo, useRef, ReactNode } from 'react';
import { ConfigProvider } from 'antd';
import { getTheme, getThemeNames, type ThemeName, type AppTheme } from '@/themes';
import { getFont, getFontFamily, availableFonts, type FontOption } from '@/themes/fonts';
import { usersApi } from '@/api/users';
import { useAuthStore } from '@/store/authStore';

interface ThemeContextType {
  currentTheme: AppTheme;
  themeName: ThemeName;
  setTheme: (themeName: ThemeName) => void;
  availableThemes: ThemeName[];
  // Font support
  currentFont: FontOption;
  fontName: string;
  setFont: (fontName: string) => void;
  availableFonts: FontOption[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'bizops-theme';
const FONT_STORAGE_KEY = 'bizops-font';

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeName;
  defaultFont?: string;
}

export function ThemeProvider({
  children,
  defaultTheme = 'default',
  defaultFont = 'system',
}: ThemeProviderProps) {
  const [themeName, setThemeName] = useState<ThemeName>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return (stored as ThemeName) || defaultTheme;
  });

  const [fontName, setFontName] = useState<string>(() => {
    const stored = localStorage.getItem(FONT_STORAGE_KEY);
    return stored || defaultFont;
  });

  const currentTheme = getTheme(themeName);
  const currentFont = getFont(fontName);

  // Per-account persistence: the logged-in user's saved font/theme follows their account.
  const userId = useAuthStore((s) => s.user?.id);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const loadedForUser = useRef<string | null>(null);

  // Write-through helper — persist a UI preference to the server (best-effort).
  const persistPreference = (patch: Record<string, string>) => {
    if (!useAuthStore.getState().isAuthenticated) return;
    usersApi.updateMyPreferences(patch).catch(() => {
      /* offline / not critical — localStorage already holds the value */
    });
  };

  // On login (or user switch), load the account's saved font/theme. Server is the source of
  // truth; localStorage is a same-device cache for instant first paint.
  useEffect(() => {
    if (!isAuthenticated || !userId) {
      loadedForUser.current = null;
      return;
    }
    if (loadedForUser.current === userId) return;
    loadedForUser.current = userId;

    let cancelled = false;
    usersApi
      .getMyPreferences()
      .then((prefs) => {
        if (cancelled || !prefs) return;
        if (typeof prefs.font === 'string' && getFont(prefs.font).name === prefs.font) {
          setFontName(prefs.font);
          localStorage.setItem(FONT_STORAGE_KEY, prefs.font);
        }
        if (typeof prefs.theme === 'string' && getThemeNames().includes(prefs.theme as ThemeName)) {
          setThemeName(prefs.theme as ThemeName);
          localStorage.setItem(THEME_STORAGE_KEY, prefs.theme);
        }
      })
      .catch(() => {
        /* ignore — fall back to localStorage/defaults */
      });
    return () => {
      cancelled = true;
    };
  }, [userId, isAuthenticated]);

  // Create merged theme with selected font
  const mergedAntdTheme = useMemo(() => {
    const fontFamily = getFontFamily(fontName);
    return {
      ...currentTheme.antdTheme,
      token: {
        ...currentTheme.antdTheme.token,
        fontFamily,
      },
    };
  }, [currentTheme, fontName]);

  // Apply CSS variables and font to document root when theme/font changes
  useEffect(() => {
    const root = document.documentElement;

    // Apply all CSS variables from theme
    Object.entries(currentTheme.cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Apply font family CSS variable
    root.style.setProperty('--font-family', getFontFamily(fontName));
    document.body.style.fontFamily = getFontFamily(fontName);

    // Sync body background with theme
    document.body.style.backgroundColor = currentTheme.cssVariables['--surface-0'];
  }, [currentTheme, fontName]);

  // Save theme to localStorage (instant) + server (per-account, persistent across devices)
  const setTheme = (newThemeName: ThemeName) => {
    setThemeName(newThemeName);
    localStorage.setItem(THEME_STORAGE_KEY, newThemeName);
    persistPreference({ theme: newThemeName });
  };

  // Save font to localStorage (instant) + server (per-account, persistent across devices)
  const setFont = (newFontName: string) => {
    setFontName(newFontName);
    localStorage.setItem(FONT_STORAGE_KEY, newFontName);
    persistPreference({ font: newFontName });
  };

  const value: ThemeContextType = {
    currentTheme,
    themeName,
    setTheme,
    availableThemes: getThemeNames(),
    currentFont,
    fontName,
    setFont,
    availableFonts,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={mergedAntdTheme}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context
 */
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
